import { ViewPlaceholder } from '@vrplatform/components';
import { useMutation } from '@vrplatform/data';
import {
  CalendarDatesIcon,
  CalendarEventIcon,
  CircleDollarIcon,
  ClipboardTextIcon,
  CreditCardIncomeIcon,
  CrossCircleIcon,
  HashtagIcon,
  HomeIcon,
  Icon,
  LoaderIcon,
  Lock2Icon,
  UserIcon,
} from '@vrplatform/icons';
import { Drawer } from '@vrplatform/components-legacy';
import { parseAsString, useQueryState } from '@vrplatform/query-params';
import {
  type Maybe,
  ensure,
  formatCurrency,
  hasValue,
  toTitleCase,
  utc,
} from '@vrplatform/utils';
import {
  Alert,
  Anchor,
  Box,
  Card,
  Group,
  HoverCard,
  LoadingOverlay,
  Progress,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  Tooltip,
  rem,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import {
  useState,
  type ComponentProps,
  type ComponentPropsWithoutRef,
  forwardRef,
  useMemo,
} from 'react';
import { AppIconById, ReservationStatusBadge } from '../../components';
import { ReservationPaymentStatusBadge } from '../../components/ReservationPaymentStatusBagde';
import { useListingDetailDrawer } from '../../views/listings/drawer';
import {
  DrawerHeader,
  DrawerInfoCard,
  DrawerInfoCardLockBadges,
  DrawerInfoCardOwnerStatementsLinks,
  DrawerIssues,
} from '../_components';
import { getDrawerInfoCardLockReasonBadges } from '../_components/DrawerInfoCardLockBadges.utils';
import {
  ReservationEditForm,
  ReservationEllipsisMenuItems,
  ReservationFinancialsSection,
  ReservationFeesSection,
  ReservationDatesPopover,
  ReservationPostingDateModal,
  ReservationDepositsSection,
  ReservationExpensesSection,
} from './_components';
import {
  formatReservationDates,
  getReservationNights,
} from './_components/ReservationDatesPopover.utils';
import {
  type Reservation,
  useLedgerReservationDetailDrawerQuery,
} from './_queries/useLedgerReservationDetailDrawerQuery';
import { useReservationDetailDrawer } from './useReservationDetailDrawer';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { ReservationDeleteModal } from './_components/ReservationDeleteModal';
import { RESERVATION_ENTITY_SECTION_ROW_LIMIT } from './_utils/reservation-entity-section-row-limit';

export const LedgerReservationDetailDrawer = () => {
  const { colorScheme } = useMantineColorScheme();
  const { colors } = useMantineTheme();

  const { opened, close, reservationId, view, setView } =
    useReservationDetailDrawer();

  const [postingDateOpene, postingDateHandlers] = useDisclosure(false);
  const [deleteOpened, deleteHandlers] = useDisclosure(false);

  const {
    isFetching,
    data,
    isLoading: isInitialLoading,
    error,
  } = useLedgerReservationDetailDrawerQuery(reservationId);

  const reservation = data?.reservation;

  const updatePaymentData = useMutation(
    (
      q,
      { reservationId, userdata }: { reservationId: string; userdata: any }
    ) => {
      return q.updateReservation({
        pk_columns: {
          id: reservationId,
        },
        _set: {
          userdata,
        },
      })?.id;
    },
    {
      invalidateQueryKeys: ['reservations'],
    }
  );

  const reservationDates = formatReservationDates(
    reservation?.checkIn,
    reservation?.checkOut
  );

  return (
    <Drawer opened={opened} onClose={close} size={550}>
      <DrawerHeader
        closeDrawer={close}
        title={
          reservation && (
            <Group mt={rem(5)} wrap="nowrap" gap="sm">
              {reservation.connection?.appId && (
                <AppIconById appId={reservation.connection.appId} size={40} />
              )}
              <Box>
                <Text size="lg" fw={500}>
                  {reservation.guestName}
                </Text>
                {reservationDates && (
                  <Text
                    size="xs"
                    c={colorScheme === 'dark' ? colors.gray[6] : 'gray'}
                    fw={400}
                  >
                    {reservationDates}
                  </Text>
                )}
              </Box>
            </Group>
          )
        }
        type="Reservation"
        loading={isInitialLoading}
        menuItems={
          view === 'overview' &&
          reservation && (
            <ReservationEllipsisMenuItems
              reservation={reservation}
              openPostingDateModal={postingDateHandlers.open}
              openDeleteModal={deleteHandlers.open}
            />
          )
        }
      />
      <LoadingOverlay
        visible={isFetching}
        loaderProps={{ size: 'sm' }}
        m="md"
      />
      {error ? (
        <ViewPlaceholder
          title={error?.message || 'Failed to load reservation'}
          description="Please try again and contact our customer support if the issue persists."
          icon="AlertTriangleIcon"
          iconColor="orange"
          backgroundColor="transparent"
          contentMaxWidth={300}
        />
      ) : !reservation && !isFetching ? (
        <ViewPlaceholder
          title="No reservation found"
          description="Has the reservation been deleted?"
          icon="CommentQuestionIcon"
          backgroundColor="transparent"
        />
      ) : view === 'edit' && reservation ? (
        <ReservationEditForm
          initialValues={{
            userData: JSON.stringify(reservation.userdata || {}, null, 2),
          }}
          onReset={() => setView('overview')}
          isLoading={isInitialLoading}
          handleSubmit={async (values) => {
            await updatePaymentData
              .mutate({
                args: {
                  reservationId: reservation.id,
                  userdata: JSON.parse(values.userData?.trim() || '{}'),
                },
              })
              .then(() => setView('overview'));
          }}
        />
      ) : (
        <>
          <ReservationPostingDateModal
            closeModal={postingDateHandlers.close}
            opened={postingDateOpene}
            reservation={reservation as any}
          />
          <ReservationDeleteModal
            closeModal={deleteHandlers.close}
            opened={deleteOpened}
            reservationId={reservation?.id}
          />
          <Content reservation={reservation} />
        </>
      )}
    </Drawer>
  );
};

type ExtendedTabs = ('financials' | 'deposits' | 'expenses' | 'fees')[];

const Content = ({ reservation }: { reservation: Maybe<Reservation> }) => {
  'use memo';

  const [tab, setTab] = useState<ExtendedTabs[number] | null>(null);

  const tabs = useMemo(() => {
    const tabs: ExtendedTabs = [];

    if (!reservation) return [];

    if (reservation.financials.length > RESERVATION_ENTITY_SECTION_ROW_LIMIT)
      tabs.push('financials');
    if (
      reservation.feesAndCommissions.length >
      RESERVATION_ENTITY_SECTION_ROW_LIMIT
    )
      tabs.push('fees');
    if (reservation.deposits.length > RESERVATION_ENTITY_SECTION_ROW_LIMIT)
      tabs.push('deposits');
    if (reservation.expenses.length > RESERVATION_ENTITY_SECTION_ROW_LIMIT)
      tabs.push('expenses');

    return tabs;
  }, [reservation]);

  if (!reservation) return null;

  const showTabs = !!tabs.length;

  return (
    <>
      {!showTabs ? (
        <Overview reservation={reservation} goToTab={setTab} />
      ) : (
        <Tabs
          value={tab || 'overview'}
          onChange={(value) => value && setTab(value as ExtendedTabs[number])}
          styles={(theme) => ({
            root: {
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              flex: 1,
            },
            panel: {
              height: '100%',
              flex: 1,
              marginTop: theme.spacing.md,
              maxWidth: '100%',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 'calc(100vh - 140px)',
            },
          })}
        >
          <Tabs.List>
            <Tabs.Tab value="overview">Overview</Tabs.Tab>
            {tabs.map((key) => {
              return (
                <Tabs.Tab key={key} value={key}>
                  {toTitleCase(key)}
                </Tabs.Tab>
              );
            })}
          </Tabs.List>

          <Tabs.Panel value="overview">
            <Stack gap={'md'} pb="md">
              <Alert icon={<Icon icon="AlertCircleIcon" size={20} />}>
                <Text size="sm">
                  This reservation contains more data than usual. You can use
                  tabs to view the data more easily.
                </Text>
              </Alert>
              <Overview reservation={reservation} goToTab={setTab} />
            </Stack>
          </Tabs.Panel>
          {tabs.map((key) => {
            return (
              <Tabs.Panel key={key} value={key}>
                {key === 'deposits' && (
                  <ReservationDepositsSection
                    currency={reservation.currency}
                    deposits={reservation.deposits}
                    goToTab={() => setTab(key)}
                    type="tab"
                  />
                )}
                {key === 'financials' && (
                  <ReservationFinancialsSection
                    reservation={reservation}
                    goToTab={() => setTab(key)}
                    type="tab"
                  />
                )}

                {key === 'fees' && (
                  <ReservationFeesSection
                    rowData={reservation.feesAndCommissions}
                    currency={reservation.currency}
                    reservation={reservation}
                    total={reservation.totals?.manager ?? 0}
                    type="tab"
                    goToTab={() => setTab(key)}
                  />
                )}

                {key === 'expenses' && (
                  <ReservationExpensesSection
                    currency={reservation.currency}
                    expenses={reservation.expenses}
                    reservationId={reservation.id}
                    goToTab={() => setTab(key)}
                    type="tab"
                  />
                )}
              </Tabs.Panel>
            );
          })}
        </Tabs>
      )}
    </>
  );
};

const Overview = ({
  reservation,
  goToTab,
}: {
  reservation: Reservation;
  goToTab: (tab: ExtendedTabs[number]) => void;
}) => {
  return (
    <Stack
      gap={'md'}
      mb="md"
      sx={{
        position: 'relative',
        flex: 1,
      }}
    >
      <DrawerIssues issues={reservation.issues} config={ISSUES_CONFIG} />

      <LedgerReservationInfoCard {...reservation} />

      {!reservation.isCancelledAndPending && <Statistics {...reservation} />}

      <ReservationFinancialsSection
        reservation={reservation}
        type="collapse"
        goToTab={() => goToTab('financials')}
      />

      <ReservationFeesSection
        rowData={reservation.feesAndCommissions}
        currency={reservation.currency}
        reservation={reservation}
        total={reservation.totals?.manager ?? 0}
        type="collapse"
        goToTab={() => goToTab('fees')}
      />

      <ReservationDepositsSection
        currency={reservation.currency}
        deposits={reservation.deposits}
        goToTab={() => goToTab('deposits')}
        type="collapse"
      />

      <ReservationExpensesSection
        currency={reservation.currency}
        expenses={reservation.expenses}
        reservationId={reservation.id}
        type="collapse"
        goToTab={() => goToTab('expenses')}
      />
    </Stack>
  );
};

export const LedgerReservationInfoCard = (reservation: {
  id: Reservation['id'];
  status: Reservation['status'];
  generalLedgerStatus?: Reservation['generalLedgerStatus'];
  listing?: Reservation['listing'];
  bookedAt?: Reservation['bookedAt'];
  lock?: Reservation['lock'];
  cancelledAt?: Reservation['cancelledAt'];
  checkIn?: Reservation['checkIn'];
  checkOut?: Reservation['checkOut'];
  confirmationCode?: Reservation['confirmationCode'];
  pmsReferenceCode?: Reservation['pmsReferenceCode'];
  userdata?: Reservation['userdata'];
  bookingPlatform?: Reservation['bookingPlatform'];
  currency?: Reservation['currency'];
  payment: Reservation['payment'];
  ownerStatementIds: Reservation['ownerStatementIds'];
  generalLedgerPostingAt: Reservation['generalLedgerPostingAt'];
}) => {
  const { open: openListingDetailDrawer } = useListingDetailDrawer();

  const userDataKeys = Object.keys(reservation?.userdata || {});

  const [, setView] = useQueryState('view', parseAsString);
  const showNightsSecondRow = useMediaQuery('(max-width: 499px)');

  const reservationNights = useMemo(
    () => getReservationNights(reservation.checkIn, reservation.checkOut),
    [reservation.checkIn, reservation.checkOut]
  );
  const lockReasonBadges = useMemo(
    () =>
      getDrawerInfoCardLockReasonBadges({
        sources: [
          {
            isLocked: reservation.lock?.status === 'locked',
            lock: reservation.lock,
          },
        ],
        fallbackBadge: {
          label: 'Locked',
          tooltip: reservation.lock?.message,
        },
      }),
    [reservation.lock, reservation.lock?.message]
  );

  const rows = useMemo(
    () =>
      ensure<
        (ComponentProps<typeof DrawerInfoCard>['rows'][number] | undefined)[]
      >([
        lockReasonBadges.length
          ? {
              icon: Lock2Icon,
              title: 'Locked due to',
              text: <DrawerInfoCardLockBadges badges={lockReasonBadges} />,
            }
          : undefined,
        {
          icon: LoaderIcon,
          title: 'Status',
          text: (
            <ReservationStatusBadge
              status={reservation.status}
              generalLedgerStatus={reservation.generalLedgerStatus}
            />
          ),
        },
        {
          icon: HomeIcon,
          title: 'Listing',
          text: reservation.listing?.id ? (
            <Anchor
              onClick={() =>
                openListingDetailDrawer(
                  reservation.listing!.id,
                  'overview',
                  'push'
                )
              }
            >
              {reservation.listing?.name ?? '-'}
            </Anchor>
          ) : null,
        },
        {
          icon: CalendarEventIcon,
          title: 'Booked',
          text: `${
            reservation.bookedAt
              ? utc(reservation.bookedAt).format('MMM DD, YYYY')
              : 'unknown date'
          }, ${toTitleCase(reservation.bookingPlatform || 'unknown channel')}`,
        },
        {
          icon: CrossCircleIcon,
          title: 'Cancelled At',
          text:
            reservation.status === 'canceled' && reservation.cancelledAt
              ? utc(reservation.cancelledAt).format('MMM DD, YYYY')
              : undefined,
        },
        {
          icon: CalendarDatesIcon,
          title: 'Dates',
          text: (
            <ReservationDatesPopover
              reservationId={reservation.id}
              checkIn={reservation.checkIn}
              checkOut={reservation.checkOut}
              lock={reservation.lock}
              showNightsInline={!showNightsSecondRow}
            />
          ),
        },
        showNightsSecondRow && reservationNights !== null
          ? {
              icon: CalendarDatesIcon,
              title: 'Nights',
              text: `${reservationNights} ${
                reservationNights === 1 ? 'night' : 'nights'
              }`,
            }
          : undefined,
        {
          icon: HashtagIcon,
          title: 'Confirmation code',
          text: reservation.confirmationCode,
        },
        {
          icon: HashtagIcon,
          title: 'PMS code',
          text: reservation.pmsReferenceCode,
        },
        {
          icon: CircleDollarIcon,
          title: 'Posting Date',
          text: reservation.generalLedgerPostingAt
            ? utc(reservation.generalLedgerPostingAt).format('MMM DD, YYYY')
            : undefined,
        },
        {
          icon: UserIcon,
          title: 'User data',
          text: !!userDataKeys.length && (
            <Anchor onClick={() => setView('edit')}>
              {userDataKeys.length} updated key
              {userDataKeys.length > 1 && 's'}
            </Anchor>
          ),
        },

        {
          icon: CreditCardIncomeIcon,
          title: 'Balance',
          text: (
            <ReservationPaymentStatusBadge
              paidStatus={reservation.payment.status}
              paymentsCentTotal={reservation.payment.received}
              reservationCentTotal={reservation.payment.expected}
              currency={reservation.currency}
            />
          ),
        },
        {
          icon: ClipboardTextIcon,
          title: 'Owner Statements',
          text: reservation.ownerStatementIds.length && (
            <DrawerInfoCardOwnerStatementsLinks
              ids={reservation.ownerStatementIds}
            />
          ),
        },
      ]).filter(hasValue),
    [
      reservation.id,
      reservation.status,
      reservation.generalLedgerStatus,
      reservation.bookedAt,
      reservation.cancelledAt,
      reservation.checkIn,
      reservation.checkOut,
      reservation.lock,
      reservationNights,
      showNightsSecondRow,
      reservation.confirmationCode,
      reservation.pmsReferenceCode,
      reservation.generalLedgerPostingAt,
      userDataKeys.length,
      reservation.payment.status,
      reservation.payment.received,
      reservation.payment.expected,
      reservation.currency,
      openListingDetailDrawer,
      reservation.listing,
      reservation.ownerStatementIds,
      reservation.bookingPlatform,
      lockReasonBadges,
      setView,
    ]
  );
  return <DrawerInfoCard mb={0} labelWidth={140} rows={rows} />;
};

const Statistics = (reservation: Reservation) => {
  const theme = useMantineTheme();

  // TOTALS
  const currency = reservation.currency;
  const guest = reservation.totals?.guest ?? 0;
  const reservationT = reservation.totals?.reservation ?? 0;
  const pmRevenue = reservation.totals.manager ?? 0;
  const taxes = reservation.totals?.tax ?? 0;
  const ownerRevenue = reservation.totals.owner ?? 0;

  // COLORS
  const ownerRevenueColor = theme.colors.blue[5];
  const pmRevenueColor = theme.colors.violet[5];
  const taxesColor = theme.colors.orange[5];
  const borderColor = theme.colors.neutral[2];

  const formatted = {
    pmRevenue: formatCurrency(pmRevenue / 100, currency),
    ownerRevenue: formatCurrency(ownerRevenue / 100, currency),
    taxes: formatCurrency(taxes / 100, currency),
    reservation: formatCurrency(reservationT / 100, currency),
  };

  return (
    <SimpleGrid
      cols={{ lg: 4 }}
      spacing={'1px'}
      sx={{
        backgroundColor: borderColor,
        border: '1px solid',
        borderColor,
        borderRadius: theme.radius.md,
        overflow: 'hidden',
        ' > div': {
          paddingInline: theme.spacing.xs,
          paddingTop: theme.spacing.sm,
          paddingBottom: theme.spacing.xs,
        },
      }}
    >
      <Stat label="Reservation">{formatted.reservation}</Stat>
      <Stat label="PM revenue" color={pmRevenueColor}>
        {formatted.pmRevenue}
      </Stat>
      <Stat label="Taxes" color={taxesColor}>
        {formatted.taxes}
      </Stat>

      <HoverCard withArrow shadow="md" position="bottom-end" arrowOffset={55}>
        <HoverCard.Target>
          <Stat label="Owner revenue" color={ownerRevenueColor}>
            {formatted.ownerRevenue}
          </Stat>
        </HoverCard.Target>
        <HoverCard.Dropdown>
          <Stack gap="xs">
            <Group wrap="nowrap" align="flex-start">
              <Text c="gray" flex={1}>
                Reservation
              </Text>
              <Text ta="right" fw={500}>
                {formatted.reservation}
              </Text>
            </Group>
            <Group wrap="nowrap" align="flex-start">
              <Text c="gray" flex={1}>
                PM revenue
              </Text>
              <Text ta="right" fw={500}>
                -{formatted.pmRevenue}
              </Text>
            </Group>
            <Group wrap="nowrap" align="flex-start">
              <Text c="gray" flex={1}>
                Taxes
              </Text>
              <Text ta="right" fw={500}>
                -{formatted.taxes}
              </Text>
            </Group>
          </Stack>
        </HoverCard.Dropdown>
      </HoverCard>

      <Card
        radius={0}
        sx={{
          gridColumn: 'span 4',
        }}
      >
        <Progress.Root size="sm">
          <Tooltip label="PM revenue" withArrow>
            <Progress.Section
              value={(pmRevenue / guest) * 100}
              color="violet"
            />
          </Tooltip>
          <Tooltip label="Taxes" withArrow>
            <Progress.Section value={(taxes / guest) * 100} color="orange" />
          </Tooltip>
          <Tooltip label="Owner revenue" withArrow>
            <Progress.Section
              value={(ownerRevenue / guest) * 100}
              color="blue"
            />
          </Tooltip>
        </Progress.Root>
      </Card>
    </SimpleGrid>
  );
};

const Stat = forwardRef<
  HTMLDivElement,
  {
    color?: string;
    label: string;
    children: string;
  } & ComponentPropsWithoutRef<'div'>
>(({ children, label, color, ...props }, ref) => {
  const theme = useMantineTheme();

  return (
    <Card radius={0} ref={ref} {...props}>
      <Group mb={rem(3)} gap={rem(4)} wrap="nowrap" align="flex-start">
        {color && (
          <Box
            mt={1.5}
            sx={{
              height: rem(8),
              borderRadius: '10px',
              width: '2px',
              flexShrink: 0,
              backgroundColor: color,
            }}
          />
        )}
        <Text c={theme.colors.gray[6]} size={rem(12)}>
          {label}
        </Text>
      </Group>
      <Text fw={500}>{children}</Text>
    </Card>
  );
});

const ISSUES_CONFIG: Record<
  Reservation['issues'][number]['code'],
  {
    title: string;
    description: string;
  }
> = {
  priorToStartDate: {
    title: "Reservations Revenue Recognition - Prior to Team's Start Date",
    description:
      "This reservation's revenue recognition is prior to the team's start date and has therefore been excluded. No active journal entries were created and won't be used on owner statements.",
  },
  inactiveListing: {
    title: 'Listing deactivated',
    description:
      'This listing was deactivated for the period of this reservation. If this was done in error, navigate to your listing and revisit the deactivation period.',
  },
  inactiveConnection: {
    title: 'Connection archived',
    description:
      'This connection of this reservation was archived. If this was done in error, navigate to your connections and reactivate it.',
  },
  unassignedAccount: {
    title: 'Reservations Revenue Recognition - Reservation accounts unmapped',
    description:
      'We have found lines in this reservation that have not been mapped to your chart of account or excluded from it yet. Please revisit your mappings in your team settings.',
  },
  lineNotFound: {
    title: 'Line not found',
    description:
      'A financial line is missing from the reservation. Please reach out to support to resolve this issue.',
  },
  unbalancedJournalEntries: {
    title: 'Unbalanced journal entries',
    description:
      'This reservation has unbalanced journal entries. Please reach out to support to resolve this issue.',
  },
  guestTotalsZero: {
    title: 'Guest totals zero',
    description:
      'The guest totals are zero. Please reach out to support to resolve this issue.',
  },
  guestTotalsMismatch: {
    title: 'Guest totals mismatch',
    description:
      'The guest totals are not matching. Please reach out to support to resolve this issue.',
  },
  listingOwnershipPeriodNotFound: {
    title: 'Missing listing ownership',
    description:
      'The reservation is not associated with a listing ownership. Add an owner to the listing to correctly recognize the revenue.',
  },
};
